// [PURPOSE]: Canonical condition-by-id lookup that checks BOTH SRD conditions
//   and playerStats.customConditions (authored via CustomConditionCreator).
//
// [Sub-plan 3B — 2026-05-29]: Closes the H1 handoff flag. Consumers that read
//   condition data by id (conditionApplicationGate callers, effectCalculator
//   numeric-effect readers, ConditionsTracker) must call this helper so custom
//   conditions from playerStats.customConditions participate alongside SRD ones.
//
// [WHY]: SRD_CONDITIONS is a static object keyed by id. Custom conditions live
//   in playerStats.customConditions (array of {id, name, ...}). Without this
//   helper, consumers hardcoded to SRD_CONDITIONS silently skip custom conditions.
//
// [INTEGRATION]:
//   - ConditionsTracker.getAllConditions — merges via this helper on render
//   - Tests: src/__tests__/3B-custom-condition-creator.test.js
//
// [RULE 22]: Extract → import → assert. This is the shared module; both
//   production consumers and test assertions import from here.

import { SRD_CONDITIONS } from '../data/srdConditions';

/**
 * Look up a condition object by id, checking SRD first then player-authored conditions.
 *
 * SRD conditions are the authoritative shape; custom conditions authored through
 * CustomConditionCreator must also follow { id, name, effects[], numericEffects?,
 * description, removedBy, sourceType, tier, severity, icon, color } shape.
 *
 * @param {string} id - Condition id, e.g. 'paralyzed', 'dazed'
 * @param {Object|null} playerStats - Character stats; may be null/undefined (defensive)
 * @returns {Object|null} Condition object or null if not found
 */
export function getConditionById(id, playerStats) {
  if (!id) return null;

  // 1. SRD lookup (keyed object — O(1))
  if (SRD_CONDITIONS[id]) return SRD_CONDITIONS[id];

  // 2. playerStats.customConditions (array authored by CustomConditionCreator)
  const custom = playerStats && playerStats.customConditions;
  if (Array.isArray(custom) && custom.length > 0) {
    const found = custom.find(c => c.id === id);
    if (found) return found;
  }

  return null;
}

/**
 * Build a merged id→condition map from SRD + playerStats.customConditions.
 * Equivalent to Object.fromEntries of the merged catalog, keyed by id.
 * Custom conditions take precedence on id collision (same rule as composerCatalogRegistry).
 *
 * Used by ConditionsTracker.getAllConditions to replace the sourcesManager-only merge.
 *
 * @param {Object|null} playerStats
 * @returns {Object} { [id]: conditionObject }
 */
export function buildConditionsMap(playerStats) {
  const base = { ...SRD_CONDITIONS };

  const custom = playerStats && playerStats.customConditions;
  if (Array.isArray(custom)) {
    for (const c of custom) {
      if (c && c.id) {
        // Normalize to display-ready shape; ensure required array fields exist
        base[c.id] = {
          name: c.name || c.id,
          severity: c.severity || 'moderate',
          effects: Array.isArray(c.effects) && c.effects.length > 0
            ? c.effects
            : (c.description ? [c.description] : ['Custom condition']),
          icon: c.icon || '🏠',
          color: c.color || 'purple',
          numericEffects: Array.isArray(c.numericEffects) ? c.numericEffects : [],
          // [Sub-plan 3B — 2026-05-30]: Preserve passiveEffects so ConditionsTracker.toggleCondition
          //   can register them with effectRegistry when the condition is applied. Pre-fix this field
          //   was omitted, making passiveEffects authored in CustomConditionCreator silently inert.
          passiveEffects: Array.isArray(c.passiveEffects) ? c.passiveEffects : [],
          autoDetected: false,
          isCustom: true,
          removedBy: c.removedBy || '',
          sourceType: c.sourceType || '',
          tier: c.tier || null,
        };
      }
    }
  }

  return base;
}
